// iter150AZ.33 + iter150AZ.34 — Centralised react-query data-fetch hooks.
//
// Wraps the heaviest GET endpoints so:
//   * Repeat navigations to the same customer/compressor reuse the
//     cached response for ``staleTime`` (30s by default — set in
//     ``index.js``).
//   * Multiple components mounting simultaneously share a single
//     in-flight request.
//   * The cache key always includes the discriminating ID, so
//     switching between two compressors doesn't bleed state.
//
// Adding a new hook here is a 5-line affair — see the existing
// patterns. Use ``enabled`` to skip the request when the id isn't
// known yet (e.g. modal closed).
//
// iter150AZ.34 — invalidation helpers added so mutation callers can
// drop the customer's cached sub-resources in a single call (used by
// the quote-create + transfer flows). Pass the QueryClient instance
// from ``useQueryClient()``.
import { useQuery } from "@tanstack/react-query";
import api from "./api";

// ─── per-compressor reads ──────────────────────────────────────────
export function useCompressorServiceForecast(serialId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["compressor-service-forecast", serialId],
    queryFn: async () => {
      const r = await api.get(
        `/compressors/${encodeURIComponent(serialId)}/service-forecast`,
      );
      return r.data;
    },
    enabled: !!serialId && enabled,
  });
}

export function useCompressorServiceHistory(serialId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["compressor-service-history", serialId],
    queryFn: async () => {
      const r = await api.get(
        `/compressors/${encodeURIComponent(serialId)}/service-history`,
      );
      return r.data;
    },
    enabled: !!serialId && enabled,
  });
}

export function useCompressorPMSchedule(serialId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["compressor-pm-schedule", serialId],
    queryFn: async () => {
      const r = await api.get(
        `/compressors/${encodeURIComponent(serialId)}/pm-schedule`,
      );
      return r.data;
    },
    enabled: !!serialId && enabled,
  });
}

export function useCompressorPhoneLogs(serialId, limit = 100, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["compressor-phone-logs", serialId, limit],
    queryFn: async () => {
      const r = await api.get(
        `/compressors/${encodeURIComponent(serialId)}/phone-logs`,
        { params: { limit } },
      );
      return r.data;
    },
    enabled: !!serialId && enabled,
  });
}

// ─── per-customer reads ────────────────────────────────────────────
export function useCustomerCompressors(customerId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["customer-compressors", customerId],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/compressors`,
      );
      return r.data;
    },
    enabled: !!customerId && enabled,
  });
}

export function useCustomerCompatibleSerials(customerId, partCode, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["customer-compatible-serials", customerId, partCode],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/compatible-serials`,
        { params: { part_code: partCode } },
      );
      return r.data;
    },
    enabled: !!customerId && !!partCode && enabled,
  });
}

// iter150AZ.34 — extended customer hooks (used by CustomerPage detail
// panel + CompressorDetailModal). Sharing the same key across both
// components means navigating Customer → Compressor → Customer in
// under 30s costs zero extra GETs.
export function useCustomerPartsPurchased(customerId, params = {}, { enabled = true } = {}) {
  // ``serial`` is the only param that meaningfully partitions the cache;
  // ``limit`` is included so a 50-row request doesn't satisfy a 200-row
  // consumer.
  const { serial = "", limit = 200 } = params;
  return useQuery({
    queryKey: ["customer-parts-purchased", customerId, serial || "", limit],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/parts-purchased`,
        { params: { ...params, limit } },
      );
      return r.data;
    },
    enabled: !!customerId && enabled,
  });
}

export function useCustomerPersons(customerId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["customer-persons", customerId],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/persons`,
      );
      return r.data;
    },
    enabled: !!customerId && enabled,
  });
}

export function useCustomerQuotes(customerId, limit = 50, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["customer-quotes", customerId, limit],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/quotes`,
        { params: { limit } },
      );
      return r.data;
    },
    enabled: !!customerId && enabled,
  });
}

export function useCustomerJournal(customerId, limit = 200, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["customer-journal", customerId, limit],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/journal`,
        { params: { limit } },
      );
      return r.data;
    },
    enabled: !!customerId && enabled,
  });
}

export function useCustomerNotes(customerId, limit = 200, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["customer-notes", customerId, limit],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/notes`,
        { params: { limit } },
      );
      return r.data;
    },
    enabled: !!customerId && enabled,
  });
}

export function useCustomerPhoneLogs(customerId, limit = 100, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["customer-phone-logs", customerId, limit],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/phone-logs`,
        { params: { limit } },
      );
      return r.data;
    },
    enabled: !!customerId && enabled,
  });
}

// ─── invalidation helpers ──────────────────────────────────────────
// Call after a mutation that mutates server-side state belonging to a
// given customer / compressor so subsequent reads pull fresh data
// instead of the (now-stale) cached response.
export function invalidateCustomer(queryClient, customerId) {
  if (!queryClient || !customerId) return;
  const keys = [
    "customer-compressors", "customer-parts-purchased", "customer-persons",
    "customer-quotes", "customer-journal", "customer-notes", "customer-phone-logs",
  ];
  keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [k, customerId] }));
}

export function invalidateCompressor(queryClient, serialId) {
  if (!queryClient || !serialId) return;
  const keys = [
    "compressor-service-forecast", "compressor-service-history",
    "compressor-pm-schedule", "compressor-phone-logs",
  ];
  keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [k, serialId] }));
}
